import { supabase } from '../supabaseClient';

export const topicsService = {
  async getTopicsData(editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get main topics data (don't throw error if not found)
      const { data: topicsData, error: topicsDataError } = await supabase
        .from('topics_data')
        .select('*')
        .eq('edition_id', edition.id)
        .single();

      if (topicsDataError && topicsDataError.code !== 'PGRST116') {
        // PGRST116 is "not found" error, which is okay
      }

      // Get topics
      const { data: topics } = await supabase
        .from('topics')
        .select('*')
        .eq('edition_id', edition.id)
        .order('display_order');

      return {
        editionInfo: {
          editionNumber: topicsData?.edition_number || '',
          themeTitle: topicsData?.theme_title || '',
          themeSubtitle: topicsData?.theme_subtitle || ''
        },
        congressHeader: {
          title: topicsData?.congress_header_title || 'Axes du Congrès',
          subtitle: topicsData?.congress_header_subtitle || ''
        },
        topics: (topics || []).map(topic => ({
          id: topic.id,
          number: topic.number,
          title: topic.title,
          description: topic.description,
          color: topic.color,
          displayOrder: topic.display_order
        }))
      };
    } catch (error) {
      throw error;
    }
  },

  async updateTopicsPageData(topicsData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const updateData = {
        edition_id: edition.id,
        edition_number: topicsData.editionInfo?.editionNumber || '',
        theme_title: topicsData.editionInfo?.themeTitle || '',
        theme_subtitle: topicsData.editionInfo?.themeSubtitle || '',
        congress_header_title: topicsData.congressHeader?.title || 'Axes du Congrès',
        congress_header_subtitle: topicsData.congressHeader?.subtitle || '',
        updated_at: new Date().toISOString()
      };

      // Try to update existing record first
      const { data: existingData } = await supabase
        .from('topics_data')
        .select('id')
        .eq('edition_id', edition.id)
        .single();

      if (existingData) {
        // Update existing record
        const { data, error } = await supabase
          .from('topics_data')
          .update(updateData)
          .eq('id', existingData.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('topics_data')
          .insert([updateData])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      throw error;
    }
  },

  async addTopic(topicData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const insertData = {
        edition_id: edition.id,
        number: topicData.number,
        title: topicData.title,
        description: topicData.description,
        color: topicData.color || '#002b5b',
        display_order: topicData.displayOrder || 0
      };

      const { data, error } = await supabase
        .from('topics')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateTopic(topicId, topicData) {
    try {
      const updateData = {
        number: topicData.number,
        title: topicData.title,
        description: topicData.description,
        color: topicData.color || '#002b5b',
        display_order: topicData.displayOrder || 0,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('topics')
        .update(updateData)
        .eq('id', topicId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteTopic(topicId) {
    try {
      const { error } = await supabase
        .from('topics')
        .delete()
        .eq('id', topicId);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }
};
